import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Receipt, Plus, Pencil, X, Loader2, ChevronDown } from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchWorkflowStatus, fetchTaxDCAs, fetchSubDCAsByDCA, fetchStates, fetchTaxItCodes, fetchItCodeGroupDetails,
    fetchGeneralTaxGrid, fetchGeneralTaxById, submitGeneralTax,
    fetchGstTaxGrid, fetchGstTaxById, submitGstTax,
    clearSubDcas, clearItCodeGroupDetails, clearGeneralById, clearGeneralSaveResult,
    clearGstById, clearGstSaveResult, resetTaxRegistration,
    selectTaxRegistration,
} from '../../slices/accountsSlice/taxRegistrationSlice';

// ── Shared primitives (matches AssignCCandDCABudget / AddNewITCode conventions) ─
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';
const readonlyCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', size = 'md', children, type = 'button' }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed';
    const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm' };
    const variants = {
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size]} ${variants[variant]}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">{message}</div>;

const Modal = ({ title, onClose, children, wide }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0 z-10">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDateForAPI = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};
// Grid/verification dates come back as "dd-Mon-yyyy" — parse explicitly rather than
// relying on native Date parsing or the picker's own numeric-month parser.
const parseDDMonYYYY = (str) => {
    if (!str || typeof str !== 'string') return null;
    const parts = str.split('-');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const monthIdx = MONTH_ABBR.findIndex((m) => m.toLowerCase() === parts[1].toLowerCase());
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || monthIdx === -1 || isNaN(year)) return null;
    return new Date(year, monthIdx, day);
};

// ═════════════════════════════════════════════════════════════════════════════
// ADD / EDIT GENERAL TAX MODAL
// ═════════════════════════════════════════════════════════════════════════════

const GeneralTaxModal = ({ row, onClose, currentUser, roleId, dcas, dcasLoading }) => {
    const dispatch = useDispatch();
    const { subDcas, subDcasLoading, generalById, generalByIdLoading, generalSaveLoading, generalSaveResult, generalSaveError } = useSelector(selectTaxRegistration);
    const isEdit = !!row;

    const [taxName, setTaxName] = useState('');
    const [applicableFrom, setApplicableFrom] = useState(new Date());
    const [dcaCode, setDcaCode] = useState('');
    const [subDcaCode, setSubDcaCode] = useState('');
    const [taxNo, setTaxNo] = useState('');
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (isEdit) dispatch(fetchGeneralTaxById(row.TaxId));
        return () => { dispatch(clearGeneralById()); dispatch(clearSubDcas()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isEdit, row?.TaxId]);

    useEffect(() => {
        if (isEdit && generalById) {
            setTaxName(generalById.TaxName || '');
            setApplicableFrom(parseDDMonYYYY(generalById.ApplicableFrom) || new Date());
            setDcaCode(generalById.TaxFor || '');
            setSubDcaCode(generalById.SubDCA || '');
            setTaxNo(generalById.TaxNo || '');
            setRemarks(generalById.Remarks || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generalById]);

    useEffect(() => {
        if (generalSaveResult) { toast.success(isEdit ? 'Tax updated successfully.' : 'Tax added successfully.'); dispatch(clearGeneralSaveResult()); onClose(); }
        if (generalSaveError) { toast.error(generalSaveError); dispatch(clearGeneralSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generalSaveResult, generalSaveError]);

    const handleDcaChange = (val) => {
        setDcaCode(val); setSubDcaCode('');
        dispatch(clearSubDcas());
        if (val) dispatch(fetchSubDCAsByDCA(val));
    };

    const busy = generalSaveLoading || (isEdit && generalByIdLoading);

    const handleSubmit = () => {
        if (!taxName.trim()) { toast.warning('Enter Tax Name.'); return; }
        if (!(applicableFrom instanceof Date)) { toast.warning('Select Applicable From date.'); return; }
        if (!isEdit) {
            if (!dcaCode) { toast.warning('Select Account Head.'); return; }
            if (!subDcaCode) { toast.warning('Select Sub Account Head.'); return; }
            if (!taxNo.trim()) { toast.warning('Enter Tax Number.'); return; }
        }
        if (!remarks.trim()) { toast.warning('Enter Remarks.'); return; }

        dispatch(submitGeneralTax({
            TaxName: taxName.trim(),
            TaxNo: taxNo,
            TaxType: 'General',
            TaxFor: dcaCode,
            SubDCA: subDcaCode,
            Remarks: remarks.trim(),
            CreatedBy: currentUser,
            Action: isEdit ? 'Update' : 'Add',
            TaxId: isEdit ? row.TaxId : 0,
            RoleId: parseInt(roleId, 10) || 0,
            ApplicableFrom: applicableFrom.toISOString(),
        }));
    };

    if (isEdit && generalByIdLoading && !generalById) {
        return <Modal title="Edit Tax" onClose={onClose}><div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div></Modal>;
    }

    return (
        <Modal title={isEdit ? `Edit Tax — ${taxNo}` : 'Add New Tax'} onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>Tax Name</Label>
                    <input className={inputCls} value={taxName} onChange={(e) => setTaxName(e.target.value)} disabled={busy} />
                </div>
                <div>
                    <Label required>Applicable From</Label>
                    {isEdit ? (
                        <input className={readonlyCls} value={formatDateForAPI(applicableFrom)} readOnly />
                    ) : (
                        <CustomDatePicker value={applicableFrom} onChange={setApplicableFrom} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} />
                    )}
                </div>
                <div>
                    <Label required>Account Head</Label>
                    {isEdit ? (
                        <input className={readonlyCls} value={dcaCode} readOnly />
                    ) : (
                        <SelectWrap loading={dcasLoading}>
                            <select className={selectCls} value={dcaCode} onChange={(e) => handleDcaChange(e.target.value)} disabled={busy}>
                                <option value="">-Please Select-</option>
                                {dcas.map((d) => <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                            </select>
                        </SelectWrap>
                    )}
                </div>
                <div>
                    <Label required>Sub Account Head</Label>
                    {isEdit ? (
                        <input className={readonlyCls} value={subDcaCode} readOnly />
                    ) : (
                        <SelectWrap loading={subDcasLoading}>
                            <select className={selectCls} value={subDcaCode} onChange={(e) => setSubDcaCode(e.target.value)} disabled={busy || !dcaCode}>
                                <option value="">-Please Select-</option>
                                {subDcas.map((s) => <option key={s.SubDCACode} value={s.SubDCACode}>{s.SubDCAIDSTR}</option>)}
                            </select>
                        </SelectWrap>
                    )}
                </div>
                <div>
                    <Label required>Tax Number</Label>
                    {isEdit ? <input className={readonlyCls} value={taxNo} readOnly /> : (
                        <input className={inputCls} value={taxNo} onChange={(e) => setTaxNo(e.target.value)} disabled={busy} />
                    )}
                </div>
                <div>
                    <Label required>Remarks</Label>
                    <textarea rows={2} className={inputCls + ' resize-none'} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} />
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={busy}>{isEdit ? 'Submit' : 'Add'}</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// ADD / EDIT GST TAX MODAL
// ═════════════════════════════════════════════════════════════════════════════

const GstTaxModal = ({ row, onClose, currentUser, roleId, dcas, dcasLoading, states, statesLoading, taxItCodes, taxItCodesLoading }) => {
    const dispatch = useDispatch();
    const {
        gstById, gstByIdLoading, gstSaveLoading, gstSaveResult, gstSaveError,
        itCodeGroupDetails, itCodeGroupDetailsLoading,
    } = useSelector(selectTaxRegistration);
    const isEdit = !!row;

    const [taxName, setTaxName] = useState('');
    const [applicableFrom, setApplicableFrom] = useState(new Date());
    const [dcaCode, setDcaCode] = useState('');
    const [taxNo, setTaxNo] = useState('');
    const [stateId, setStateId] = useState('');
    const [walletName, setWalletName] = useState('');
    const [address, setAddress] = useState('');
    const [itCodeId, setItCodeId] = useState('');
    const [ledgerName, setLedgerName] = useState('');
    const [openingBalance, setOpeningBalance] = useState('');
    const [balanceAsOnDate, setBalanceAsOnDate] = useState(new Date());
    const [ledgerValueType, setLedgerValueType] = useState('');
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (isEdit) dispatch(fetchGstTaxById(row.TaxId));
        return () => { dispatch(clearGstById()); dispatch(clearItCodeGroupDetails()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isEdit, row?.TaxId]);

    useEffect(() => {
        if (isEdit && gstById) {
            setTaxName(gstById.TaxName || '');
            setApplicableFrom(parseDDMonYYYY(gstById.AppFrom) || new Date());
            setDcaCode(gstById.TaxFor || '');
            setTaxNo(gstById.TaxNo || '');
            setWalletName(gstById.WalletName || '');
            setAddress(gstById.Address || '');
            setRemarks(gstById.Remarks || '');
            const ledger = gstById.Ledger;
            if (ledger) {
                setLedgerName(ledger.Name || '');
                setOpeningBalance(ledger.Balance ?? '');
                setBalanceAsOnDate(parseDDMonYYYY(ledger.Date) || new Date());
                setLedgerValueType(ledger.ValueType || '');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gstById]);

    useEffect(() => {
        if (gstSaveResult) { toast.success(isEdit ? 'GST updated successfully.' : 'GST added successfully.'); dispatch(clearGstSaveResult()); onClose(); }
        if (gstSaveError) { toast.error(gstSaveError); dispatch(clearGstSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gstSaveResult, gstSaveError]);

    const handleItCodeChange = (val) => {
        setItCodeId(val);
        dispatch(clearItCodeGroupDetails());
        if (val) dispatch(fetchItCodeGroupDetails(val));
    };

    const busy = gstSaveLoading || (isEdit && gstByIdLoading);

    const handleSubmit = () => {
        if (!taxName.trim()) { toast.warning('Enter Tax Name.'); return; }
        if (!(applicableFrom instanceof Date)) { toast.warning('Select Applicable From date.'); return; }
        if (!isEdit) {
            if (!dcaCode) { toast.warning('Select Account Head.'); return; }
            if (!taxNo.trim()) { toast.warning('Enter Tax Number.'); return; }
            if (!stateId) { toast.warning('Select State.'); return; }
            if (!walletName.trim()) { toast.warning('Enter Wallet Name.'); return; }
            if (!itCodeId) { toast.warning('Select IT Code.'); return; }
        }
        if (!address.trim()) { toast.warning('Enter Wallet Address.'); return; }
        if (!remarks.trim()) { toast.warning('Enter Remarks.'); return; }
        if (!ledgerName.trim()) { toast.warning('Enter Ledger Name.'); return; }
        if (!openingBalance || isNaN(parseFloat(openingBalance))) { toast.warning('Enter Opening Balance.'); return; }
        if (!(balanceAsOnDate instanceof Date)) { toast.warning('Select Balance As On Date.'); return; }
        if (balanceAsOnDate < applicableFrom) { toast.warning('Balance As On Date must be on or after Applicable From date.'); return; }
        if (!ledgerValueType) { toast.warning('Select Ledger Value Type.'); return; }

        dispatch(submitGstTax({
            TaxName: taxName.trim(),
            TaxNo: taxNo,
            TaxType: 'GST',
            TaxFor: dcaCode,
            Remarks: remarks.trim(),
            CreatedBy: currentUser,
            Action: isEdit ? 'Update' : 'Add',
            TaxId: isEdit ? row.TaxId : 0,
            RoleId: parseInt(roleId, 10) || 0,
            ApplicableFrom: applicableFrom.toISOString(),
            StateId: parseInt(stateId, 10) || 0,
            WalletName: walletName.trim(),
            Address: address.trim(),
            TaxItID: parseInt(itCodeId, 10) || 0,
            NatureGroupId: itCodeGroupDetails?.NatureGroupId || '',
            GroupId: itCodeGroupDetails?.GroupId || '',
            SubGroupId: itCodeGroupDetails?.SubGroupId || 0,
            LedgerName: ledgerName.trim(),
            LedgerValueType: ledgerValueType,
            OpeningBalance: parseFloat(openingBalance),
            BalanceAsOnDate: formatDateForAPI(balanceAsOnDate),
        }));
    };

    if (isEdit && gstByIdLoading && !gstById) {
        return <Modal title="Edit GST" onClose={onClose} wide><div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div></Modal>;
    }

    return (
        <Modal title={isEdit ? `Edit GST — ${taxNo}` : 'Add New GST'} onClose={onClose} wide>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <Label required>Tax Name</Label>
                    <input className={inputCls} value={taxName} onChange={(e) => setTaxName(e.target.value)} disabled={busy} />
                </div>
                <div>
                    <Label required>Applicable From</Label>
                    {isEdit ? (
                        <input className={readonlyCls} value={formatDateForAPI(applicableFrom)} readOnly />
                    ) : (
                        <CustomDatePicker value={applicableFrom} onChange={setApplicableFrom} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} />
                    )}
                </div>
                <div>
                    <Label required>Account Head</Label>
                    {isEdit ? (
                        <input className={readonlyCls} value={dcaCode} readOnly />
                    ) : (
                        <SelectWrap loading={dcasLoading}>
                            <select className={selectCls} value={dcaCode} onChange={(e) => setDcaCode(e.target.value)} disabled={busy}>
                                <option value="">-Please Select-</option>
                                {dcas.map((d) => <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                            </select>
                        </SelectWrap>
                    )}
                </div>
                <div>
                    <Label required>GST Number</Label>
                    {isEdit ? <input className={readonlyCls} value={taxNo} readOnly /> : (
                        <input className={inputCls} value={taxNo} onChange={(e) => setTaxNo(e.target.value)} disabled={busy} />
                    )}
                </div>
                <div>
                    <Label required>State</Label>
                    {isEdit ? (
                        <input className={readonlyCls} value={gstById?.State || ''} readOnly />
                    ) : (
                        <SelectWrap loading={statesLoading}>
                            <select className={selectCls} value={stateId} onChange={(e) => setStateId(e.target.value)} disabled={busy}>
                                <option value="">-Please Select-</option>
                                {states.map((s) => <option key={s.State_ID} value={s.State_ID}>{s.State_Name}</option>)}
                            </select>
                        </SelectWrap>
                    )}
                </div>
                <div>
                    <Label required>Wallet Name</Label>
                    {isEdit ? <input className={readonlyCls} value={walletName} readOnly /> : (
                        <input className={inputCls} value={walletName} onChange={(e) => setWalletName(e.target.value)} disabled={busy} />
                    )}
                </div>
                {!isEdit && (
                    <div>
                        <Label required>IT Code</Label>
                        <SelectWrap loading={taxItCodesLoading}>
                            <select className={selectCls} value={itCodeId} onChange={(e) => handleItCodeChange(e.target.value)} disabled={busy}>
                                <option value="">-Please Select-</option>
                                {taxItCodes.map((i) => <option key={i.Itid} value={i.Itid}>{i.ItName}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                )}
                {!isEdit && itCodeId && (
                    <>
                        <div>
                            <Label>Nature Of Group</Label>
                            <input className={readonlyCls} value={itCodeGroupDetailsLoading ? '…' : (itCodeGroupDetails?.NatureGroupName || '')} readOnly />
                        </div>
                        <div>
                            <Label>Group</Label>
                            <input className={readonlyCls} value={itCodeGroupDetailsLoading ? '…' : (itCodeGroupDetails?.GroupName || '')} readOnly />
                        </div>
                        <div>
                            <Label>Sub Group</Label>
                            <input className={readonlyCls} value={itCodeGroupDetailsLoading ? '…' : (itCodeGroupDetails?.SubGroupName || '')} readOnly />
                        </div>
                    </>
                )}
                <div>
                    <Label required>Ledger Name</Label>
                    <input className={inputCls} value={ledgerName} onChange={(e) => setLedgerName(e.target.value)} disabled={busy} />
                </div>
                <div>
                    <Label required>Opening Balance</Label>
                    <input className={inputCls} value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} disabled={busy} inputMode="decimal" />
                </div>
                <div>
                    <Label required>Balance As On Date</Label>
                    <CustomDatePicker value={balanceAsOnDate} onChange={setBalanceAsOnDate} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} minDate={applicableFrom instanceof Date ? applicableFrom : null} />
                </div>
                <div className="md:col-span-2">
                    <Label required>Remarks</Label>
                    <textarea rows={2} className={inputCls + ' resize-none'} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} />
                </div>
                <div className="md:col-span-1">
                    <Label required>Wallet Address</Label>
                    <textarea rows={2} className={inputCls + ' resize-none'} value={address} onChange={(e) => setAddress(e.target.value)} disabled={busy || isEdit} />
                </div>
                <div className="md:col-span-3">
                    <Label required>Ledger Value Type</Label>
                    <div className="flex gap-4 mt-1">
                        {['Credit', 'Debit'].map((t) => (
                            <label key={t} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                <input type="radio" name="gstLedgerValueType" checked={ledgerValueType === t} onChange={() => setLedgerValueType(t)} disabled={busy} />
                                {t}
                            </label>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={busy}>{isEdit ? 'Submit' : 'Add'}</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// GENERAL TAX TAB
// ═════════════════════════════════════════════════════════════════════════════

const GeneralTaxTab = ({ currentUser, roleId, dcas, dcasLoading }) => {
    const dispatch = useDispatch();
    const { generalGrid, generalGridLoading, generalGridError } = useSelector(selectTaxRegistration);
    const [showAdd, setShowAdd] = useState(false);
    const [editRow, setEditRow] = useState(null);

    useEffect(() => { dispatch(fetchGeneralTaxGrid()); }, [dispatch]);

    const refresh = () => dispatch(fetchGeneralTaxGrid());

    return (
        <div className="space-y-4">
            <Card>
                <div className="p-4 flex justify-end">
                    <Btn onClick={() => setShowAdd(true)}><Plus className="h-3.5 w-3.5" />New Tax</Btn>
                </div>
            </Card>
            <Card>
                {generalGridLoading ? (
                    <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : generalGridError ? (
                    <div className="py-10 text-center text-sm text-rose-500">{generalGridError}</div>
                ) : generalGrid.length === 0 ? (
                    <EmptyState message="No Taxes found." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Tax Number', 'Tax Name', 'Account Head', 'Sub Account Head', 'Remarks', 'Applicable From', ''].map((h) => (
                                        <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {generalGrid.map((row) => (
                                    <tr key={row.TaxId} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.TaxNo}</td>
                                        <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.TaxName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.TaxFor}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.SubDCA}</td>
                                        <td className="px-3 py-2.5 text-gray-500 text-xs">{row.Remarks}</td>
                                        <td className="px-3 py-2.5 text-gray-500 text-xs">{row.ApplicableFrom}</td>
                                        <td className="px-3 py-2.5">
                                            <button onClick={() => setEditRow(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Edit">
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {showAdd && (
                <GeneralTaxModal onClose={() => { setShowAdd(false); refresh(); }} currentUser={currentUser} roleId={roleId} dcas={dcas} dcasLoading={dcasLoading} />
            )}
            {editRow && (
                <GeneralTaxModal row={editRow} onClose={() => { setEditRow(null); refresh(); }} currentUser={currentUser} roleId={roleId} dcas={dcas} dcasLoading={dcasLoading} />
            )}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// GST TAX TAB
// ═════════════════════════════════════════════════════════════════════════════

const GstTaxTab = ({ currentUser, roleId, dcas, dcasLoading, states, statesLoading, taxItCodes, taxItCodesLoading }) => {
    const dispatch = useDispatch();
    const { gstGrid, gstGridLoading, gstGridError } = useSelector(selectTaxRegistration);
    const [showAdd, setShowAdd] = useState(false);
    const [editRow, setEditRow] = useState(null);

    useEffect(() => { dispatch(fetchGstTaxGrid()); }, [dispatch]);

    const refresh = () => dispatch(fetchGstTaxGrid());

    return (
        <div className="space-y-4">
            <Card>
                <div className="p-4 flex justify-end">
                    <Btn onClick={() => setShowAdd(true)}><Plus className="h-3.5 w-3.5" />New Tax</Btn>
                </div>
            </Card>
            <Card>
                {gstGridLoading ? (
                    <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                ) : gstGridError ? (
                    <div className="py-10 text-center text-sm text-rose-500">{gstGridError}</div>
                ) : gstGrid.length === 0 ? (
                    <EmptyState message="No GST Taxes found." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                    {['Tax Number', 'Tax Name', 'Account Head', 'State', 'Remarks', 'Applicable From', ''].map((h) => (
                                        <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                {gstGrid.map((row) => (
                                    <tr key={row.TaxId} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.TaxNo}</td>
                                        <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.TaxName}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.TaxFor}</td>
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.State}</td>
                                        <td className="px-3 py-2.5 text-gray-500 text-xs">{row.Remarks}</td>
                                        <td className="px-3 py-2.5 text-gray-500 text-xs">{row.ApplicableFrom}</td>
                                        <td className="px-3 py-2.5">
                                            <button onClick={() => setEditRow(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Edit">
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {showAdd && (
                <GstTaxModal
                    onClose={() => { setShowAdd(false); refresh(); }} currentUser={currentUser} roleId={roleId}
                    dcas={dcas} dcasLoading={dcasLoading} states={states} statesLoading={statesLoading}
                    taxItCodes={taxItCodes} taxItCodesLoading={taxItCodesLoading}
                />
            )}
            {editRow && (
                <GstTaxModal
                    row={editRow} onClose={() => { setEditRow(null); refresh(); }} currentUser={currentUser} roleId={roleId}
                    dcas={dcas} dcasLoading={dcasLoading} states={states} statesLoading={statesLoading}
                    taxItCodes={taxItCodes} taxItCodesLoading={taxItCodesLoading}
                />
            )}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const TABS = [
    { id: 'general', mid: 118, label: 'Other Taxes' },
    { id: 'gst', mid: 120, label: 'GST' },
];

const TaxRegistration = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || userData?.uid || '';

    const { workflowStatus, dcas, dcasLoading, states, statesLoading, taxItCodes, taxItCodesLoading } = useSelector(selectTaxRegistration);

    const [activeTab, setActiveTab] = useState('');

    useEffect(() => {
        dispatch(fetchWorkflowStatus(roleId));
        dispatch(fetchTaxDCAs());
        dispatch(fetchStates());
        dispatch(fetchTaxItCodes());
        return () => { dispatch(resetTaxRegistration()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const visibleTabs = useMemo(
        () => TABS.filter((t) => workflowStatus.find((w) => w.MID === t.mid)?.WorkflowExist),
        [workflowStatus]
    );

    useEffect(() => {
        if (!activeTab && visibleTabs.length > 0) setActiveTab(visibleTabs[0].id);
    }, [activeTab, visibleTabs]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Receipt className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{menuData?.name || 'Tax Registration'}</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Register General and GST taxes</p>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex gap-0 overflow-x-auto">
                        {visibleTabs.map(({ id, label }) => {
                            const active = activeTab === id;
                            return (
                                <button key={id} onClick={() => setActiveTab(id)}
                                    className={`px-5 py-4 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${active ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'}`}>
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6">
                {visibleTabs.length === 0 ? (
                    <EmptyState message="You do not have access to any tax registration workflow." />
                ) : activeTab === 'general' ? (
                    <GeneralTaxTab currentUser={currentUser} roleId={roleId} dcas={dcas} dcasLoading={dcasLoading} />
                ) : activeTab === 'gst' ? (
                    <GstTaxTab currentUser={currentUser} roleId={roleId} dcas={dcas} dcasLoading={dcasLoading} states={states} statesLoading={statesLoading} taxItCodes={taxItCodes} taxItCodesLoading={taxItCodesLoading} />
                ) : null}
            </div>
        </div>
    );
};

export default TaxRegistration;
